import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

// Импорт существующего компонента
import { DeviceApiKeysList } from '../../../components/registration/DeviceApiKeysList';

// Импорты сервисов и типов
import { api } from '../../../services/api';
import type { Device } from '../../../types';

interface DeviceApiKeysTabProps {
  // Пока без пропов
}

export const DeviceApiKeysTab: React.FC<DeviceApiKeysTabProps> = () => {
  const { t } = useTranslation();
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);

  // Запрос списка устройств для выбора
  const {
    data: devicesData,
    isLoading: isLoadingDevices
  } = useQuery({
    queryKey: ['devices-for-api-keys'],
    queryFn: () => api.getDevices({ page: 1, size: 100 }),
  });

  const devices = devicesData?.items || [];
  const selectedDevice = devices.find(d => d.id === selectedDeviceId);

  const handleDeviceChange = (deviceId: number) => {
    setSelectedDeviceId(deviceId);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('devices.management.tabs.apiKeys')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('devices.management.apiKeysDescription')}
        </Typography>

        {/* Информационное сообщение */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('devices.management.apiKeysInfo')}
          </Typography>
        </Alert>
      </Box>

      {/* Выбор устройства */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="device-select-label">
            {t('devices.selectDevice')}
          </InputLabel>
          <Select
            labelId="device-select-label"
            value={selectedDeviceId || ''}
            label={t('devices.selectDevice')}
            onChange={(e) => handleDeviceChange(Number(e.target.value))}
            disabled={isLoadingDevices}
          >
            {isLoadingDevices ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('common.loading')}
              </MenuItem>
            ) : devices.length === 0 ? (
              <MenuItem disabled>
                {t('devices.noDevicesAvailable')}
              </MenuItem>
            ) : (
              devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name} ({device.location || t('devices.noLocation')})
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Paper>

      {/* Компонент управления API ключами */}
      {selectedDevice ? (
        <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
          <DeviceApiKeysList
            deviceId={selectedDevice.id}
            deviceName={selectedDevice.name}
          />
        </Paper>
      ) : (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('devices.management.selectDeviceForApiKeys')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('devices.management.selectDeviceForApiKeysDescription')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}; 